// Builds the Field Services Snapshot Report as a Word document using the
// already-installed `docx` package. Content mirrors SnapshotPrintContent.jsx —
// keep the two in sync via the shared helpers in snapshotFormat.js.
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeightRule,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from "docx";
import {
  yn,
  fmtText,
  fmtNum,
  fmtDateString,
  fmtTimestamp,
  fmtTime,
  fmtGps,
  finalDtw,
  INSPECTION_CHECKLIST,
  setbackRows,
  buildWqGrid,
  wqMetaSummary,
  reportTitle,
  BLANK_WQ_PARAMETERS,
  BLANK_WQ_READING_COLUMNS,
  BLANK_WL_ROWS,
  BLANK_METER_ROWS,
} from "./snapshotFormat";

const FONT = "Times New Roman";

// Page margins in twips (1440 = 1"). Narrow-ish sides so tables get room.
const PAGE_MARGIN = { top: 1008, bottom: 1008, left: 864, right: 864 };

// Row height for handwriting rows on the blank template (~0.35")
const BLANK_ROW_HEIGHT = 500;

const text = (content, opts = {}) =>
  new TextRun({ text: content, font: FONT, size: 22, ...opts });

const para = (children, opts = {}) =>
  new Paragraph({ children, spacing: { after: 60 }, ...opts });

const labelValue = (label, value) =>
  para([text(`${label}: `, { bold: true }), text(String(value ?? "—"))]);

// Shaded band heading; keepNext so a heading never strands at a page bottom.
const sectionTitle = (title) =>
  new Paragraph({
    children: [text(`  ${title.toUpperCase()}`, { bold: true, size: 22 })],
    spacing: { before: 260, after: 120 },
    keepNext: true,
    shading: { fill: "E8E8E8" },
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 4, color: "999999" },
    },
  });

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
const NO_BORDERS = {
  top: NO_BORDER,
  bottom: NO_BORDER,
  left: NO_BORDER,
  right: NO_BORDER,
};

// Default interior padding for table cells (twips)
const CELL_MARGINS = { top: 50, bottom: 50, left: 90, right: 90 };

// Lay out items in a borderless N-column grid. Each item is either a
// [label, value] pair (rendered "label: value") or a plain string.
const itemsGrid = (items, cols = 3) => {
  const cellContent = (item) => {
    if (!item) return new Paragraph({});
    if (typeof item === "string") return para([text(item)]);
    return labelValue(item[0], item[1]);
  };
  const rows = [];
  for (let i = 0; i < items.length; i += cols) {
    const slice = items.slice(i, i + cols);
    while (slice.length < cols) slice.push(null);
    rows.push(
      new TableRow({
        children: slice.map(
          (item) =>
            new TableCell({
              borders: NO_BORDERS,
              width: {
                size: Math.floor(100 / cols),
                type: WidthType.PERCENTAGE,
              },
              children: [cellContent(item)],
            })
        ),
      })
    );
  }
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    margins: { top: 20, bottom: 20, left: 0, right: 90 },
    rows,
  });
};

// Bordered data table. opts.blankRows renders that many empty, taller rows
// (for handwriting on the printed blank template) before any data rows.
const dataTable = (headers, rows, opts = {}) => {
  const bodyRow = (cells, tall) =>
    new TableRow({
      ...(tall
        ? { height: { value: BLANK_ROW_HEIGHT, rule: HeightRule.ATLEAST } }
        : {}),
      children: cells.map(
        (cell) =>
          new TableCell({
            verticalAlign: VerticalAlign.CENTER,
            children: [
              new Paragraph({
                spacing: { after: 0 },
                children: [text(String(cell ?? "—"), { size: 18 })],
              }),
            ],
          })
      ),
    });

  const blankRows = Array.from({ length: opts.blankRows || 0 }, () =>
    bodyRow(new Array(headers.length).fill(""), true)
  );

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    margins: CELL_MARGINS,
    rows: [
      new TableRow({
        tableHeader: true,
        children: headers.map(
          (header) =>
            new TableCell({
              shading: { fill: "DDDDDD" },
              verticalAlign: VerticalAlign.CENTER,
              children: [
                new Paragraph({
                  spacing: { after: 0 },
                  children: [text(header, { bold: true, size: 18 })],
                }),
              ],
            })
        ),
      }),
      ...blankRows,
      ...rows.map((row) => bodyRow(row, false)),
    ],
  });
};

const scaleImage = (width, height, maxWidth = 600, maxHeight = 520) => {
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
};

// Letterhead: logo left, District identity right, single rule underneath.
const letterhead = (logo) =>
  new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            borders: NO_BORDERS,
            width: { size: 35, type: WidthType.PERCENTAGE },
            verticalAlign: VerticalAlign.CENTER,
            children: [
              logo
                ? new Paragraph({
                    children: [
                      new ImageRun({
                        data: logo.arrayBuffer,
                        transformation: scaleImage(
                          logo.width,
                          logo.height,
                          170,
                          65
                        ),
                      }),
                    ],
                  })
                : new Paragraph({}),
            ],
          }),
          new TableCell({
            borders: NO_BORDERS,
            width: { size: 65, type: WidthType.PERCENTAGE },
            verticalAlign: VerticalAlign.CENTER,
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 20 },
                children: [
                  text("Barton Springs/Edwards Aquifer Conservation District", {
                    bold: true,
                    size: 24,
                  }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 0 },
                children: [
                  text("1124 Regal Row, Austin, TX 78748  •  (512) 282-8441", {
                    size: 18,
                    color: "444444",
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });

export const buildSnapshotDocx = async ({ data, photos, logo, blank }) => {
  const { inspection, waterLevels, waterQuality, meterReadings, documents } =
    data;
  const wqMeta = wqMetaSummary(waterQuality);
  const firstWl = waterLevels[0];

  // The paper form's "Time In" isn't captured by the app; approximate it with
  // the earliest same-day reading time when one exists.
  const earliestReading = [...waterLevels, ...waterQuality]
    .map((row) => row.collect_timestamp)
    .sort((a, b) => new Date(a) - new Date(b))[0];
  const timeIn = blank
    ? "______________"
    : earliestReading
    ? fmtTime(earliestReading)
    : "—";

  const children = [];

  // --- Letterhead + title ---
  children.push(
    letterhead(logo),
    new Paragraph({
      spacing: { after: 40 },
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 8, color: "000000" },
      },
      children: [],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 160, after: 220 },
      children: [
        text(`BSEACD ${reportTitle(inspection)}`, { bold: true, size: 32 }),
      ],
    })
  );

  // --- Inspection details ---
  children.push(
    sectionTitle("Inspection Details"),
    itemsGrid([
      ["Inspection Date", fmtDateString(inspection.inspection_date)],
      ["Time In (first reading)", timeIn],
      ["Inspection Type", fmtText(inspection.ir_type_desc)],
      ["Inspectors", fmtText(inspection.inspected_by)],
      ["District Well #", fmtText(inspection.district_well_id)],
      ["State Well #", fmtText(inspection.state_well_num)],
      ["Well Name", fmtText(inspection.well_name)],
      [
        "Classification",
        [inspection.well_class_name, inspection.well_subclass_name]
          .filter(Boolean)
          .join(" / ") || "—",
      ],
      ["Primary Use", fmtText(inspection.primary_use_desc)],
      ["Well Status", fmtText(inspection.well_status_desc)],
    ])
  );

  // --- Owner ---
  children.push(
    sectionTitle("Permittee / Owner"),
    itemsGrid(
      [
        ["Permittee / Owner", fmtText(inspection.owner_primary_contact_name)],
        ["Phone", fmtText(inspection.owner_primary_contact_phone)],
        [
          "Physical Address",
          [inspection.physical_location_address, inspection.city]
            .filter(Boolean)
            .join(", ") || "—",
        ],
        [
          "GPS Coordinates of Well",
          fmtGps(inspection.lat_dd, inspection.lon_dd),
        ],
      ],
      2
    )
  );

  // --- Site checks ---
  children.push(
    sectionTitle("Site Checks"),
    itemsGrid([
      [
        "WQ Samples Collected",
        blank ? "Yes / No" : waterQuality.length ? "Yes" : "No",
      ],
      ["Photos", blank ? "Yes / No" : photos.length ? "Yes" : "No"],
      [
        "E-line Access Port",
        blank ? "Yes / No" : firstWl ? yn(firstWl.has_eline_access_port) : "—",
      ],
      [
        "Conductor Pipe",
        blank ? "Yes / No" : firstWl ? yn(firstWl.has_conductor_pipe) : "—",
      ],
      ["Sampling Spigot", fmtText(inspection.sampling_spigot_desc)],
      ["Metered", yn(inspection.is_metered)],
    ])
  );

  // --- Meter ---
  children.push(
    sectionTitle("Meter"),
    itemsGrid(
      [
        ["Meter Serial No", fmtText(inspection.meter_sn)],
        ["Meter Nickname", fmtText(inspection.meter_nickname)],
      ],
      2
    )
  );
  if (blank) {
    children.push(
      dataTable(
        ["Meter", "Month", "Previous Reading", "Reading", "Calculated Gallons"],
        [],
        { blankRows: BLANK_METER_ROWS }
      )
    );
  } else if (meterReadings.length) {
    children.push(
      dataTable(
        ["Meter", "Month", "Previous Reading", "Reading", "Calculated Gallons"],
        meterReadings.map((mr) => [
          fmtText(mr.meter_id),
          `${mr.c_month}/${mr.c_year}`,
          fmtNum(mr.prev_meter_reading),
          fmtNum(mr.meter_reading),
          fmtNum(mr.calculated_gallons),
        ])
      )
    );
  }

  // --- Surface completion ---
  children.push(
    sectionTitle("Surface Completion"),
    itemsGrid([
      ["Slab", yn(inspection.is_slab)],
      ["Slab Thickness", fmtNum(inspection.slab_thickness_inches, " in")],
      [
        "Slab Dimensions",
        inspection.slab_length_inches || inspection.slab_width_inches
          ? `${fmtNum(inspection.slab_length_inches)} x ${fmtNum(
              inspection.slab_width_inches
            )} in`
          : "—",
      ],
      ["Steel Sleeve", yn(inspection.is_steel_sleeve)],
      ["Sleeve Height", fmtNum(inspection.sleeve_height_inches, " in")],
      ["Sleeve OD", fmtNum(inspection.sleeve_outside_diameter_inches, " in")],
      ["Casing Type", fmtText(inspection.casing_type_desc)],
      ["Casing OD", fmtNum(inspection.casing_outside_diameter_inches, " in")],
      ["Casing Stickup", fmtNum(inspection.casing_height_inches, " in")],
    ]),
    labelValue(
      "Surface Completion Notes",
      fmtText(inspection.surface_completion_notes)
    )
  );

  // --- Equipment & treatment ---
  children.push(
    sectionTitle("Equipment & Treatment"),
    itemsGrid([
      ["Storage Tank", yn(inspection.storage_tank)],
      ["Disinfection/Treatment", fmtText(inspection.other_treatments_desc)],
      ["Treatment Notes", fmtText(inspection.treatments_notes)],
      ["Flow Rate", fmtNum(inspection.flow_rate_gpm, " gpm")],
      ["Pump Size", fmtNum(inspection.pump_motor_size_hp, " hP")],
      ["Pump Type", fmtText(inspection.pump_type_desc)],
      ["System Type", fmtText(inspection.system_type_desc)],
      ["Pump Depth", fmtNum(inspection.pump_depth_ft, " ft")],
      [
        "Total Borehole Depth",
        fmtNum(inspection.total_borehole_depth_ft, " ft"),
      ],
    ])
  );

  // --- Setbacks ---
  children.push(
    sectionTitle("Well Location Setbacks"),
    dataTable(
      ["Well Location", "Required Distance (ft)", "Actual"],
      setbackRows(inspection).map((row) => [
        row.label,
        row.required,
        row.actual,
      ])
    )
  );
  if (inspection.setback_notes) {
    children.push(labelValue("Setback Notes", inspection.setback_notes));
  }

  // --- Checklist ---
  children.push(
    sectionTitle("Inspection Checklist"),
    itemsGrid(
      INSPECTION_CHECKLIST.map(
        ({ field, label }) => `${inspection[field] ? "☑" : "☐"} ${label}`
      )
    )
  );

  // --- Water levels ---
  const WL_HEADERS = [
    "Time",
    "Method",
    "Measured From",
    "MP Ht (in)",
    "DTW 1 (ft)",
    "DTW 2 (ft)",
    "DTW 3 (ft)",
    "Final DTW (ft)",
    "Pumping",
    "E-line Port",
    "Cond. Pipe",
  ];
  children.push(sectionTitle("Water Levels"));
  if (blank) {
    children.push(dataTable(WL_HEADERS, [], { blankRows: BLANK_WL_ROWS }));
  } else if (waterLevels.length) {
    children.push(
      dataTable(
        WL_HEADERS,
        waterLevels.map((wl) => [
          fmtTime(wl.collect_timestamp),
          fmtText(wl.wl_meas_method_desc),
          fmtText(wl.wl_meas_from_desc),
          fmtNum(wl.wl_meas_point_height_in),
          fmtNum(wl.wl_meas_val_ft),
          fmtNum(wl.wl_meas_val_ft_2),
          fmtNum(wl.wl_meas_val_ft_3),
          fmtNum(finalDtw(wl.wl_meas_val_ft, wl.wl_meas_point_height_in)),
          yn(wl.is_pumping),
          yn(wl.has_eline_access_port),
          yn(wl.has_conductor_pipe),
        ])
      )
    );
    const wlRemarks = waterLevels
      .map((wl) => [wl.sampled_by, wl.remarks].filter(Boolean).join(": "))
      .filter(Boolean)
      .join("; ");
    if (wlRemarks) children.push(labelValue("WL Remarks", wlRemarks));
  } else {
    children.push(
      para([text("No water level measurements recorded during this visit.")])
    );
  }

  // --- Water quality ---
  children.push(sectionTitle("Water Quality Parameters"));
  if (blank) {
    children.push(
      para([
        text(
          "At least 3 readings at 5 min intervals — this purges the well prior to sample collection.",
          { italics: true, size: 18 }
        ),
      ]),
      dataTable(
        [
          "Parameter",
          ...Array.from(
            { length: BLANK_WQ_READING_COLUMNS },
            (_, idx) => `Reading ${idx + 1}`
          ),
        ],
        BLANK_WQ_PARAMETERS.map((param) => [
          param,
          ...new Array(BLANK_WQ_READING_COLUMNS).fill(""),
        ])
      )
    );
  } else if (waterQuality.length) {
    const { eventTimes, parameters } = buildWqGrid(waterQuality);
    children.push(
      dataTable(
        ["Parameter", ...eventTimes.map(fmtTime)],
        parameters.map((param) => [param.label, ...param.results])
      )
    );
    if (wqMeta) {
      children.push(
        itemsGrid(
          [
            ["Well Purged", wqMeta.purged],
            ["Lab", wqMeta.lab],
            ["Equipment", wqMeta.equipment],
            ["Sampled By", wqMeta.sampledBy],
          ],
          2
        )
      );
      if (wqMeta.notes) children.push(labelValue("WQ Notes", wqMeta.notes));
    }
  } else {
    children.push(
      para([text("No water quality samples recorded during this visit.")])
    );
  }

  // --- Notes ---
  children.push(
    sectionTitle("Other Notes or Well Construction Issues"),
    para([text(inspection.field_notes || "—")], {
      spacing: { after: 240 },
    })
  );

  // --- Documents ---
  if (documents.length) {
    children.push(sectionTitle("Documents On File For This Visit"));
    documents.forEach((doc) => {
      children.push(
        para([
          text(
            `• ${doc.original_filename}${doc.notes ? ` — ${doc.notes}` : ""}`
          ),
        ])
      );
    });
  }

  children.push(
    new Paragraph({
      alignment: AlignmentType.RIGHT,
      spacing: { before: 300 },
      children: [
        text(
          blank
            ? `Blank field sheet template generated ${new Date().toLocaleDateString(
                "en-US"
              )} from the BSEACD Field Services system.`
            : `Generated from the BSEACD Field Services database on ${new Date().toLocaleDateString(
                "en-US"
              )}. Record created ${fmtTimestamp(
                inspection.created_timestamp
              )}. Water level, water quality, and photo data include all entries recorded for this well on the inspection date; well details reflect the current well record.`,
          { size: 16, italics: true, color: "555555" }
        ),
      ],
    })
  );

  // --- Photo appendix ---
  if (photos.length) {
    children.push(
      new Paragraph({ children: [], pageBreakBefore: true }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          text(
            `Inspection Photos — ${fmtText(
              inspection.district_well_id
            )} — ${fmtDateString(inspection.inspection_date)}`,
            { bold: true, size: 28 }
          ),
        ],
      })
    );
    photos.forEach((photo) => {
      children.push(
        new Paragraph({
          keepNext: true,
          spacing: { before: 160 },
          children: [
            new ImageRun({
              data: photo.arrayBuffer,
              transformation: scaleImage(photo.width, photo.height),
            }),
          ],
        }),
        para([
          text(
            [
              photo.original_filename,
              photo.photo_taken_by ? `Taken by ${photo.photo_taken_by}` : null,
              photo.notes,
            ]
              .filter(Boolean)
              .join(" — "),
            { italics: true, size: 18 }
          ),
        ])
      );
    });
  }

  const doc = new Document({
    styles: {
      default: {
        document: { run: { font: FONT, size: 22 } },
      },
    },
    sections: [{ properties: { page: { margin: PAGE_MARGIN } }, children }],
  });

  return Packer.toBlob(doc);
};
